/**
 * Level analysis used by the level lab script, the endless generator and the tests:
 * how wide the solution window is, how likely a random placement wins, and a small solver.
 */
import { MASS_SPECS, RECEIVER_CATCH, WORLD_H, WORLD_W } from './constants';
import { canPlace, receiverNeeds, traceLevel, wins } from './level';
import type { BeamTrace } from './physics';
import type { Rng } from './rng';
import type { Level, Mass, MassKind } from './types';

/** Shortest distance from a point to a traced beam path. */
export function pathDistance(trace: BeamTrace, x: number, y: number): number {
  let best = Infinity;
  for (const run of trace.runs) {
    const p = run.pts;
    for (let i = 0; i + 3 < p.length; i += 2) {
      const ax = p[i]!;
      const ay = p[i + 1]!;
      const ex = p[i + 2]! - ax;
      const ey = p[i + 3]! - ay;
      const l2 = ex * ex + ey * ey;
      let t = l2 > 0 ? ((x - ax) * ex + (y - ay) * ey) / l2 : 0;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const dx = ax + t * ex - x;
      const dy = ay + t * ey - y;
      const d = dx * dx + dy * dy;
      if (d < best) best = d;
    }
  }
  return Math.sqrt(best);
}

/**
 * A continuous "how far from winning" score (0 = won). Unlit telescopes add the distance by
 * which the nearest suitable beams miss them; wrong-colour arrivals add a penalty.
 */
export function objective(level: Level, placed: readonly Mass[]): number {
  const t = traceLevel(level, placed);
  if (t.won) return 0;
  let score = 0;
  level.receivers.forEach((r, j) => {
    const st = t.receivers[j]!;
    if (st.lit) return;
    score += 50;
    const need = receiverNeeds(r);
    const dists: number[] = [];
    for (const b of t.beams) {
      // Beams whose colour (at any point) is part of the mix this telescope wants.
      const useful = b.runs.some(
        (run) => (run.color & r.color) !== 0 && (run.color & ~r.color) === 0,
      );
      if (!useful) continue;
      if (b.reason === 'receiver' && b.target === j) dists.push(0);
      else dists.push(Math.max(0, pathDistance(b, r.x, r.y) - RECEIVER_CATCH));
    }
    dists.sort((a, b) => a - b);
    for (let k = 0; k < need; k++) score += dists[k] ?? 400;
    if ((st.mask & ~r.color) !== 0) score += 120;
  });
  return score;
}

/**
 * Solution width: the largest distance every mass of the solution can be moved, in any of 16
 * directions, before the level stops being solved. A precision measure in world units.
 */
export function solutionWidth(level: Level, solution: readonly Mass[], limit = 60): number {
  let width = limit;
  for (let i = 0; i < solution.length; i++) {
    for (let k = 0; k < 16; k++) {
      const a = (k / 16) * Math.PI * 2;
      let ok = 0;
      for (let s = 1; s <= width; s++) {
        const moved = solution.map((m, j) =>
          j === i ? { ...m, x: m.x + Math.cos(a) * s, y: m.y + Math.sin(a) * s } : m,
        );
        if (
          !canPlace(level, moved, moved[i]!.kind, moved[i]!.x, moved[i]!.y, i) ||
          !wins(level, moved)
        )
          break;
        ok = s;
      }
      width = Math.min(width, ok);
      if (width === 0) return 0;
    }
  }
  return width;
}

/** True if the solution still wins with each mass nudged by eps in 8 directions. */
export function isRobust(level: Level, solution: readonly Mass[], eps: number): boolean {
  if (!wins(level, solution)) return false;
  for (let i = 0; i < solution.length; i++) {
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * Math.PI * 2;
      const moved = solution.map((m, j) =>
        j === i ? { ...m, x: m.x + Math.cos(a) * eps, y: m.y + Math.sin(a) * eps } : m,
      );
      if (!wins(level, moved)) return false;
    }
  }
  return true;
}

/** Inventory as a flat list of kinds. */
export function inventoryKinds(level: Level): MassKind[] {
  const out: MassKind[] = [];
  for (const [k, n] of Object.entries(level.inventory))
    for (let i = 0; i < (n ?? 0); i++) out.push(k as MassKind);
  return out;
}

/** A random valid position for a mass, or null after many tries. */
export function randomSpot(
  level: Level,
  placed: readonly Mass[],
  kind: MassKind,
  rng: Rng,
): Mass | null {
  const r = MASS_SPECS[kind].radius;
  for (let tries = 0; tries < 200; tries++) {
    const x = rng.range(r, WORLD_W - r);
    const y = rng.range(r, WORLD_H - r);
    if (canPlace(level, placed, kind, x, y)) return { kind, x, y };
  }
  return null;
}

/**
 * Chance that a random player placement wins: each tray item is used with probability 1/2 and
 * dropped at a uniformly random valid spot.
 */
export function randomSolveRate(level: Level, rng: Rng, samples: number): number {
  const kinds = inventoryKinds(level);
  if (kinds.length === 0) return wins(level, []) ? 1 : 0;
  let hits = 0;
  for (let s = 0; s < samples; s++) {
    let chosen = kinds.filter(() => rng.chance(0.5));
    if (chosen.length === 0) chosen = [rng.pick(kinds)];
    const placed: Mass[] = [];
    for (const k of chosen) {
      const m = randomSpot(level, placed, k, rng);
      if (m) placed.push(m);
    }
    if (wins(level, placed)) hits++;
  }
  return hits / samples;
}

/**
 * Search for a winning placement of exactly these kinds: random restarts followed by a
 * shrinking pattern search on the objective. Returns null when nothing is found in budget.
 */
export function solve(
  level: Level,
  kinds: readonly MassKind[],
  rng: Rng,
  restarts = 40,
): Mass[] | null {
  if (kinds.length === 0) return wins(level, []) ? [] : null;
  for (let rs = 0; rs < restarts; rs++) {
    let cur: Mass[] = [];
    let failed = false;
    for (const k of kinds) {
      const m = randomSpot(level, cur, k, rng);
      if (!m) {
        failed = true;
        break;
      }
      cur.push(m);
    }
    if (failed) continue;
    let f = objective(level, cur);
    let step = 120;
    while (step >= 1.5 && f > 0) {
      let improved = false;
      for (let i = 0; i < cur.length && f > 0; i++) {
        for (let k = 0; k < 8; k++) {
          const a = (k / 8) * Math.PI * 2 + rng.range(-0.3, 0.3);
          const m = cur[i]!;
          const nx = m.x + Math.cos(a) * step;
          const ny = m.y + Math.sin(a) * step;
          if (!canPlace(level, cur, m.kind, nx, ny, i)) continue;
          const trial = cur.map((q, j) => (j === i ? { ...q, x: nx, y: ny } : q));
          const g = objective(level, trial);
          if (g < f) {
            cur = trial;
            f = g;
            improved = true;
            if (f === 0) break;
          }
        }
      }
      if (!improved) step *= 0.6;
    }
    if (f === 0) return cur;
  }
  return null;
}

/** All distinct sub-multisets of the inventory, lightest first. */
export function inventorySubsets(level: Level): MassKind[][] {
  const entries = Object.entries(level.inventory).filter(([, n]) => (n ?? 0) > 0) as [
    MassKind,
    number,
  ][];
  const out: MassKind[][] = [];
  const rec = (i: number, acc: MassKind[]) => {
    if (i === entries.length) {
      if (acc.length > 0) out.push([...acc]);
      return;
    }
    const [k, n] = entries[i]!;
    for (let c = 0; c <= n; c++) {
      for (let j = 0; j < c; j++) acc.push(k);
      rec(i + 1, acc);
      for (let j = 0; j < c; j++) acc.pop();
    }
  };
  rec(0, []);
  const w = (s: MassKind[]) => s.reduce((t, k) => t + MASS_SPECS[k].weight, 0);
  return out.sort((a, b) => w(a) - w(b) || a.length - b.length);
}
